
import React from 'react';
import { formatCurrency } from '@/lib/utils';

interface InvoiceProjectProgressBarProps {
  estimateTotal: number;
  totalPaidPreviously: number;
  currentInvoiceAmount: number;
  remainingToInvoice: number;
}

const InvoiceProjectProgressBar: React.FC<InvoiceProjectProgressBarProps> = ({
  estimateTotal,
  totalPaidPreviously,
  currentInvoiceAmount,
  remainingToInvoice
}) => {
  // Calculate percentages
  const previousPercentage = estimateTotal > 0 ? (totalPaidPreviously / estimateTotal) * 100 : 0;
  const currentPercentage = estimateTotal > 0 ? (currentInvoiceAmount / estimateTotal) * 100 : 0;
  const remainingPercentage = estimateTotal > 0 ? (remainingToInvoice / estimateTotal) * 100 : 0;
  
  // Ensure percentages don't exceed 100%
  const normalizedPreviousPercentage = Math.min(previousPercentage, 100);
  const normalizedCurrentPercentage = Math.min(currentPercentage, 100 - normalizedPreviousPercentage);
  const normalizedRemainingPercentage = Math.max(0, 100 - normalizedPreviousPercentage - normalizedCurrentPercentage);

  // Format currency

  if (estimateTotal === 0) {
    return null;
  }

  return (
    <div style={{ 
      marginBottom: '24px', 
      padding: '16px', 
      backgroundColor: '#f9fafb', 
      border: '1px solid #e5e7eb', 
      borderRadius: '6px',
      pageBreakInside: 'avoid',
      breakInside: 'avoid'
    }}>
      <h4 style={{ 
        fontSize: '14px', 
        fontWeight: '600', 
        marginBottom: '12px', 
        color: '#374151' 
      }}>
        Project Invoice Progress
      </h4>
      
      {/* Progress Bar */}
      <div style={{ 
        width: '100%', 
        backgroundColor: '#e5e7eb', 
        borderRadius: '9999px', 
        height: '24px', 
        marginBottom: '16px' 
      }}>
        <div style={{ 
          height: '24px', 
          borderRadius: '9999px', 
          display: 'flex' 
        }}>
          {/* Previously Paid */}
          {normalizedPreviousPercentage > 0 && (
            <div style={{ 
              backgroundColor: '#22c55e',
              borderRadius: normalizedPreviousPercentage === 100 ? '9999px' : '9999px 0 0 9999px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: 'white',
              fontWeight: '500',
              width: `${normalizedPreviousPercentage}%`
            }}>
              {normalizedPreviousPercentage > 15 ? `${Math.round(normalizedPreviousPercentage)}%` : ''}
            </div>
          )}
          
          {/* Current Invoice */}
          {normalizedCurrentPercentage > 0 && (
            <div style={{ 
              backgroundColor: '#3b82f6',
              borderRadius: normalizedPreviousPercentage === 0 && normalizedRemainingPercentage === 0 ? '9999px' : 
                           normalizedPreviousPercentage === 0 ? '9999px 0 0 9999px' :
                           normalizedRemainingPercentage === 0 ? '0 9999px 9999px 0' : '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: 'white',
              fontWeight: '500',
              width: `${normalizedCurrentPercentage}%`
            }}>
              {normalizedCurrentPercentage > 15 ? `${Math.round(normalizedCurrentPercentage)}%` : ''}
            </div>
          )}
          
          {/* Remaining */}
          {normalizedRemainingPercentage > 0 && (
            <div style={{ 
              backgroundColor: '#9ca3af',
              borderRadius: '0 9999px 9999px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: 'white',
              fontWeight: '500',
              width: `${normalizedRemainingPercentage}%`
            }}>
              {normalizedRemainingPercentage > 15 ? `${Math.round(normalizedRemainingPercentage)}%` : ''}
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: '16px', 
        fontSize: '12px' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ 
            width: '12px', 
            height: '12px', 
            backgroundColor: '#22c55e', 
            borderRadius: '2px', 
            marginRight: '8px' 
          }}></div>
          <div>
            <div style={{ fontWeight: '500' }}>Previously Paid</div>
            <div style={{ color: '#6b7280' }}>{formatCurrency(totalPaidPreviously)}</div>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ 
            width: '12px', 
            height: '12px', 
            backgroundColor: '#3b82f6', 
            borderRadius: '2px', 
            marginRight: '8px' 
          }}></div>
          <div>
            <div style={{ fontWeight: '500' }}>This Invoice</div>
            <div style={{ color: '#6b7280' }}>{formatCurrency(currentInvoiceAmount)}</div>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ 
            width: '12px', 
            height: '12px', 
            backgroundColor: '#9ca3af', 
            borderRadius: '2px', 
            marginRight: '8px' 
          }}></div>
          <div>
            <div style={{ fontWeight: '500' }}>Remaining</div>
            <div style={{ color: '#6b7280' }}>{formatCurrency(remainingToInvoice)}</div>
          </div>
        </div>
      </div>

      {/* Total Project Value */}
      <div style={{ 
        marginTop: '12px', 
        paddingTop: '12px', 
        borderTop: '1px solid #e5e7eb', 
        fontSize: '14px' 
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          fontWeight: '600' 
        }}>
          <span>Total Project Value:</span>
          <span>{formatCurrency(estimateTotal)}</span>
        </div>
      </div>
    </div>
  );
};

export default InvoiceProjectProgressBar;
